import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Card, CardContent } from '../components/ui/card';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const SharedIdeaPage = () => {
  const { sharedIdeaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [sharedIdea, setSharedIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchSharedIdea = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/shared-idea/${sharedIdeaId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('This shared idea has expired or does not exist.');
          } else {
            setError('Failed to load shared idea.');
          }
          return;
        }
        const data = await response.json();
        setSharedIdea(data);
      } catch (err) {
        setError('Failed to load shared idea.');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedIdea();
  }, [sharedIdeaId]);

  const handleAcceptIdea = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/shared/${sharedIdeaId}&action=accept`);
      return;
    }

    setAccepting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/shared-idea/${sharedIdeaId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user.id
        },
        body: JSON.stringify({ user_email: user.email })
      });

      if (response.ok) {
        navigate('/app?tab=ideas&filter=shared');
      } else {
        setError('Failed to accept idea. Please try again.');
      }
    } catch (err) {
      setError('Failed to accept idea. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Oops!</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Tickler
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if this is an Ideas List
  const isIdeasList = sharedIdea?.ticker?.includes('Ideas List') || sharedIdea?.is_ideas_list;
  const tickerCount = sharedIdea?.all_tickers?.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <span className="font-bold text-xl">
              <span className="text-slate-800">TICK</span>
              <span className="text-orange-500">LER</span>
            </span>
          </div>
          {!user && (
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Sign In
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Shared By Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
            </svg>
          </div>
          <div>
            <p className="text-sm text-blue-800">
              <span className="font-semibold">{sharedIdea.sender_name || sharedIdea.sender_email}</span> shared this {isIdeasList ? 'ideas list' : 'investment idea'} with you
            </p>
            <p className="text-xs text-blue-600">
              {new Date(sharedIdea.shared_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </p>
          </div>
        </div>

        {/* Main Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Ticker/Title */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-800 mb-1">
                  {isIdeasList ? (
                    <>📋 Ideas List <span className="text-lg font-normal text-slate-500">({tickerCount} tickers)</span></>
                  ) : (
                    <>${sharedIdea.ticker?.toUpperCase()}</>
                  )}
                </h1>
              </div>
              {!isIdeasList && (
                <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                  Investment Idea
                </span>
              )}
            </div>

            {/* Personal Message */}
            {sharedIdea.personal_message && (
              <div className="bg-slate-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
                <p className="text-slate-700 italic">"{sharedIdea.personal_message}"</p>
                <p className="text-sm text-slate-500 mt-2">— {sharedIdea.sender_name || 'A friend'}</p>
              </div>
            )}

            {/* Content */}
            <div className="prose prose-slate max-w-none mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {isIdeasList ? 'List Summary' : 'Investment Summary'}
              </h3>
              <p className="text-slate-600 whitespace-pre-wrap">{sharedIdea.content}</p>
            </div>

            {/* Tickers List for Ideas List */}
            {isIdeasList && sharedIdea.all_tickers && sharedIdea.all_tickers.length > 0 && (
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Tickers in this list:</h3>
                <div className="flex flex-wrap gap-2">
                  {sharedIdea.all_tickers.map((ticker, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-slate-100 text-slate-700 text-sm font-mono rounded-full hover:bg-slate-200 transition-colors"
                    >
                      ${ticker}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleAcceptIdea}
            disabled={accepting}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
          >
            {accepting ? 'Adding...' : user ? 'Add to My Dashboard' : 'Sign Up & Save This Idea'}
          </button>
          
          {!user && (
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-all"
            >
              Already have an account? Sign In
            </button>
          )}
        </div>

        {/* Benefits for Non-Users */}
        {!user && (
          <div className="mt-12 text-center">
            <h2 className="text-xl font-semibold text-slate-800 mb-6">Why join Tickler?</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="p-4">
                <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">Save Ideas</h3>
                <p className="text-sm text-slate-600">Organize investment ideas from X, emails, and newsletters in one place</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">AI-Powered Insights</h3>
                <p className="text-sm text-slate-600">Get instant summaries and analysis of investment ideas</p>
              </div>
              <div className="p-4">
                <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">Share & Collaborate</h3>
                <p className="text-sm text-slate-600">Share ideas with friends and track what others are watching</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-12 py-6 text-center text-sm text-slate-500">
        <p>© 2025 Tickler. IDEAS WORTH VETTING.</p>
      </footer>
    </div>
  );
};

export default SharedIdeaPage;
